/* eslint-disable jira/filename-case */
import Url from 'url-parse';
import { CAPABILITY_HEADER_NAME } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { getCapability } from '@jira/platform__observability-capabilities/src/services/active-capability-tracker/index.tsx';
import { getEnhancedCapabilityHeader } from '@jira/platform__app-framework-ufo-capability-header/src/index.tsx';
import { getActiveTraceHttpRequestHeaders } from '@atlaskit/react-ufo/experience-trace-id-context';
import { expVal } from '@atlassian/jira-feature-experiments';
import { getActiveTraceHttpHeaders } from '@atlassian/trace-context/getActiveTraceHttpHeaders';
/**
 * Generates headers for observability purposes based on the provided URL.
 * It includes capability and tracing information if available.
 * Returns null if no headers are generated.
 */
export const makeObservabilityHeaders = (
	includeCapability = true,
): {
	headers: Record<string, string>;
} | null => {
	const headers: Record<string, string> = {};
	if (includeCapability) {
		const capability = getCapability();
		const capabilityHeader = getEnhancedCapabilityHeader(capability);
		if (capabilityHeader) {
			headers[CAPABILITY_HEADER_NAME] = capabilityHeader;
		}
	}
	// Check React UFO trace context first - Set by React UFO experiences
	const tracingHeaders = getActiveTraceHttpRequestHeaders();
	if (tracingHeaders !== null) {
		Object.assign(headers, tracingHeaders);
	} else {
		// Check platform trace context next - Set by BM3 and UFOv1 experiences
		const platformActiveTraceHeaders = getActiveTraceHttpHeaders();
		if (platformActiveTraceHeaders) {
			Object.assign(headers, platformActiveTraceHeaders);
		}
	}
	if (Object.keys(headers).length === 0) return null;
	return {
		headers,
	};
};
/**
 * Conditionally applies generated observability headers to request options.
 * Headers are added only if the request is made to the same hostname (in client-side context)
 * or unconditionally if on the server-side, enhancing cross-origin request observability.
 */
export const applyObservabilityHeaders = (url: string, options: RequestInit): RequestInit => {
	const endpointUrl = new Url(url);

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	if (!__SERVER__ && endpointUrl.hostname && endpointUrl.hostname !== window.location.hostname) {
		return options;
	}
	const extraHeaders = expVal('jpo-42550-improve-capability-tracking', 'isEnabled', false)
		? makeObservabilityHeaders(!new Headers(options.headers).has(CAPABILITY_HEADER_NAME))
		: makeObservabilityHeaders();
	if (extraHeaders === null) return options;
	return {
		...options,
		headers: {
			...options.headers,
			...extraHeaders.headers,
		},
	};
};
